// Shared TWAP-derived USD pricing for vault positions - used by both useVaultsList (the listing
// page) and useVaultDetail (the per-vault page). Extracted rather than duplicated since both need
// the exact same "price whichever side isn't a direct reference asset off the pool's own TWAP
// ratio" derivation - see useVaultsList.js's own doc comment for the full reasoning.
import { ZERO_ADDR } from '@/lib/contracts'

export const PRICE_PRECISION = 10n ** 36n // matches PriceLib.PRECISION (contracts/vaults/libraries/PriceLib.sol)

// ClearingV3/ClearingV4 both expose this exact signature (IClearing) - one ABI works for either
// venue, so pricing doesn't need the venue branch the vault-body reads do.
export const CLEARING_TWAP_VALUE_ABI = [
  { type: 'function', name: 'twapValue', stateMutability: 'view',
    inputs: [{ type: 'address' }, { type: 'uint256' }, { type: 'uint256' }], outputs: [{ type: 'uint256' }] },
]

export function clearingAddressOrZero(addr) {
  return addr ?? ZERO_ADDR
}

/// Derives USD pricing for a vault's two-sided position.
///   t0/t1            - token0/token1 addresses, lowercased
///   decimals0/1       - token decimals
///   amt0/amt1         - decimal-adjusted token amounts (floats, via toNumber)
///   usdxAddr/horizonAddr/hasHorizonPrice/horizonUsd - this app's only two direct USD references
///   unitPriceRaw      - raw `twapValue(vault, 1e36, 0)` result (BigInt), or null/undefined if
///                        the pool has no TWAP history yet (fresh/unconfigured pool)
/// Returns { price0, price1, priced0, priced1, tvlUsd, fullyPriced }.
export function priceVaultPosition({
  t0, t1, decimals0, decimals1, amt0, amt1,
  usdxAddr, horizonAddr, hasHorizonPrice, horizonUsd, unitPriceRaw,
}) {
  const ref0 = t0 === usdxAddr ? 1 : (t0 === horizonAddr && hasHorizonPrice ? horizonUsd : null)
  const ref1 = t1 === usdxAddr ? 1 : (t1 === horizonAddr && hasHorizonPrice ? horizonUsd : null)

  // Decimal-adjusted token1-per-token0 TWAP rate, only when the pool has enough observation
  // history for a TWAP read (fresh/unconfigured pools revert here, same shape as
  // VaultOracleHook.observe() before it's warmed) - null in that case, not a crash.
  const token1PerToken0 = unitPriceRaw != null
    ? (Number(unitPriceRaw) / 1e36) * 10 ** (decimals0 - decimals1)
    : null

  let price0 = ref0, price1 = ref1, bothPriced = false
  if (ref0 != null && ref1 != null) {
    bothPriced = true
  } else if (ref0 != null && token1PerToken0 != null) {
    price1 = ref0 * token1PerToken0
    bothPriced = true
  } else if (ref1 != null && token1PerToken0 != null && token1PerToken0 > 0) {
    price0 = ref1 / token1PerToken0
    bothPriced = true
  }

  const priced0 = price0 != null
  const priced1 = price1 != null
  const tvlUsd = (priced0 ? amt0 * price0 : 0) + (priced1 ? amt1 * price1 : 0)
  const fullyPriced = bothPriced || (amt0 === 0 && amt1 === 0)

  return { price0, price1, priced0, priced1, tvlUsd, fullyPriced }
}
